import React, { useState, useMemo } from 'react';
import { format, isToday, parseISO } from 'date-fns';
import { clsx } from 'clsx';
import { useToastStore } from '@/stores';
import {
  useCountdown,
  useAutoSendTime,
} from '@/hooks/recall-report';
import { useSettingsQueries } from '@/hooks/settings';
import { ConfirmDialog } from '@/features/recall-report/components/ConfirmDialog';
import { AddPositionDialog } from '@/features/recall-report/components/AddPositionDialog';
import { ReportStatusBar } from './ReportStatusBar';
import { ReportActionBar } from './ReportActionBar';
import { useAuth } from '@/hooks/auth/use-auth';
import { ReportTableSkeleton } from './ReportTableSkeleton';
import { DEFAULT_SEND_HOUR, DEFAULT_SEND_MINUTE } from '@/utils/report-format';
import type { LocalEdit, NewItem } from '@/stores/recall-report/recall-report-store';

export const VELOCITY_COUNTERPARTY = 'Velocity-EXT';

// ---- Generic types for report data ----

interface ReportItemBase {
  id: number;
  symbol_id: string;
  counterparty_product_id: string;
  original_counterparty_product_id: string;
  original_quantity: number;
  quantity: number;
  is_modified: boolean;
  is_deleted: boolean;
  is_manually_added: boolean;
  modified_by_email: string | null;
  modified_at: string | null;
}

interface ReportDetailBase {
  id: number;
  counterparty: string;
  status: string;
  created_at: string;
  updated_at: string;
  sent_by_email: string | null;
  sent_at: string | null;
  inventory_state: number;
  items: ReportItemBase[];
}

// ---- Store adapter interface ----

export interface ReportStoreAdapter {
  isEditing: boolean;
  localEdits: Record<number, LocalEdit>;
  newItems: NewItem[];
  selectedReportId: number | null;
  enterEditMode: () => void;
  cancelEdit: () => void;
  updateItemQuantity: (id: number, quantity: number, originalQuantity: number) => void;
  updateItemCounterpartyProductId: (id: number, value: string, originalValue: string) => void;
  toggleItemDeleted: (id: number) => void;
  markItemDeleted: (id: number) => void;
  hasChanges: () => boolean;
  getChangedItems: (items: Array<{ id: number; quantity: number; counterparty_product_id: string }>) => Array<{ id: number; quantity: number; is_deleted: boolean; counterparty_product_id?: string }>;
  addNewItem: (cpid: string, symbolId: string, qty: number) => void;
  removeNewItem: (tempId: string) => void;
  updateNewItemQuantity: (tempId: string, quantity: number) => void;
  updateNewItemCounterpartyProductId: (tempId: string, counterpartyProductId: string) => void;
  getNewItemsPayload: () => Array<{ counterparty_product_id: string; symbol_id: string; quantity: number }>;
}

// ---- Mutation hooks adapter ----

export interface ReportMutations {
  updateItems: {
    mutate: (params: { reportId: number; items: Array<{ id: number; quantity: number; is_deleted: boolean; counterparty_product_id?: string }>; creates: Array<{ counterparty_product_id: string; symbol_id: string; quantity: number }> }) => void;
    isPending: boolean;
  };
  reset: {
    mutate: (params: { reportId: number }) => void;
    isPending: boolean;
  };
  send: {
    mutate: (params: { reportId: number }) => void;
    isPending: boolean;
  };
  download: (reportId: number, filename?: string) => void;
}

type SortState = { id: string; desc: boolean } | null;

// ---- Table component type ----

export type ReportTableComponent<T extends ReportDetailBase = ReportDetailBase> = React.ComponentType<{
  report: T | undefined;
  isLoading: boolean;
  error: unknown;
  sorting: SortState;
  onSortChange: (sorting: SortState) => void;
  isEditing: boolean;
  localEdits: Record<number, LocalEdit>;
  newItems: NewItem[];
  isMutating: boolean;
  onToggleItemDeleted: (id: number) => void;
  onUpdateItemQuantity: (id: number, quantity: number, originalQuantity: number) => void;
  onUpdateItemCounterpartyProductId: (id: number, value: string, originalValue: string) => void;
  onRemoveNewItem: (tempId: string) => void;
  onUpdateNewItemQuantity: (tempId: string, quantity: number) => void;
  onUpdateNewItemCounterpartyProductId: (tempId: string, counterpartyProductId: string) => void;
  searchQuery?: string;
}>;

// ---- Props ----

interface ReportViewProps<T extends ReportDetailBase = ReportDetailBase> {
  report: T | undefined;
  isLoading: boolean;
  error: unknown;
  store: ReportStoreAdapter;
  mutations: ReportMutations;
  TableComponent: ReportTableComponent<T>;
  searchQuery?: string;
  resetDialogMessage: string;
  sendDialogMessage: string;
  filenamePrefix: string;
  inventoryCounterparty?: string;
  counterparty: string;
  quantityField?: 'quantity' | 'velocity_l_quantity';
}

export function ReportView<T extends ReportDetailBase>({
  report,
  isLoading,
  error,
  store,
  mutations,
  TableComponent,
  searchQuery,
  resetDialogMessage,
  sendDialogMessage,
  filenamePrefix,
  inventoryCounterparty,
  counterparty,
  quantityField,
}: ReportViewProps<T>) {
  const addToast = useToastStore((s) => s.addToast);
  // LR-257: editing/sending a report requires the `send_reports` capability.
  const canManage = useAuth().hasPermission('send_reports');

  // Local state
  const [sorting, setSorting] = useState<SortState>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showAddPosition, setShowAddPosition] = useState(false);

  const isDraftToday = report
    ? report.status === 'draft' && isToday(parseISO(report.created_at))
    : false;

  // Settings & countdown
  const { data: settings } = useSettingsQueries();
  const sendHour = settings?.velocity_reports_send_hour ?? DEFAULT_SEND_HOUR;
  const sendMinute = settings?.velocity_reports_send_minute ?? DEFAULT_SEND_MINUTE;
  const autoSendTime = useAutoSendTime(sendHour, sendMinute);
  const rawCountdown = useCountdown(autoSendTime);
  const countdown = isDraftToday ? rawCountdown : null;

  const isMutating = mutations.updateItems.isPending || mutations.reset.isPending || mutations.send.isPending;

  // Enter edit mode and pre-populate localEdits for server-deleted items
  const handleEnterEditMode = () => {
    setSorting(null);
    store.enterEditMode();
    if (report?.items) {
      for (const item of report.items) {
        if (item.is_deleted) {
          store.markItemDeleted(item.id);
        }
      }
    }
  };

  const handleSave = () => {
    if (!store.selectedReportId || !report?.items) return;
    const items = store.getChangedItems(report.items);
    const creates = store.getNewItemsPayload();

    // Client-side PID validation mirrors the backend rule so users see the
    // error without a round-trip and we don't send an obviously bad request.
    const hasEmptyEditedPid = items.some(
      (i) => i.counterparty_product_id !== undefined && !i.counterparty_product_id.trim(),
    );
    const hasEmptyNewPid = creates.some((c) => !c.counterparty_product_id.trim());
    if (hasEmptyEditedPid || hasEmptyNewPid) {
      addToast('error', 'Product ID cannot be empty');
      return;
    }

    mutations.updateItems.mutate({ reportId: store.selectedReportId, items, creates });
  };

  const handleReset = () => {
    if (!store.selectedReportId) return;
    setShowResetConfirm(false);
    mutations.reset.mutate({ reportId: store.selectedReportId });
  };

  const handleSend = () => {
    if (!store.selectedReportId) return;
    setShowSendConfirm(false);
    mutations.send.mutate({ reportId: store.selectedReportId });
  };

  const handleDownload = () => {
    if (!store.selectedReportId) return;
    const dateStr = report
      ? format(parseISO(report.created_at), 'yyyyMMdd')
      : '';
    const filename = dateStr
      ? `${dateStr}-${filenamePrefix}.csv`
      : undefined;
    mutations.download(store.selectedReportId, filename);
  };

  // Compute existingProductIds for dialog filtering
  const existingProductIds = useMemo(() => {
    const fromReport = (report?.items ?? []).map((i) => i.counterparty_product_id);
    const fromNew = store.newItems.map((ni) => ni.counterparty_product_id);
    return [...fromReport, ...fromNew];
  }, [report?.items, store.newItems]);

  // Cheap item count (sorting doesn't change count — let the table own sorted items)
  const itemCount = useMemo(() => {
    const existingCount = (report?.items ?? []).filter((item) => {
      if (store.isEditing && store.localEdits[item.id]?.isDeleted !== undefined) {
        return !store.localEdits[item.id].isDeleted;
      }
      return !item.is_deleted;
    }).length;
    return existingCount + (store.isEditing ? store.newItems.length : 0);
  }, [store.isEditing, report?.items, store.newItems, store.localEdits]);

  return (
    <div className={clsx('flex h-full flex-col', store.isEditing && 'bg-blue-50')}>
      {report && (
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-2.5 text-sm">
          <ReportStatusBar
            counterpartyName={
              settings?.default_counterparty_id === report.counterparty
                ? settings.default_counterparty_name
                : report.counterparty.charAt(0).toUpperCase() + report.counterparty.slice(1)
            }
            status={report.status}
            updatedAt={report.updated_at}
            sentByEmail={report.sent_by_email}
            sentAt={report.sent_at}
            itemCount={itemCount}
          />
          <ReportActionBar
            canManage={canManage}
            isEditing={store.isEditing}
            isDraftToday={isDraftToday}
            isMutating={isMutating}
            isResetting={mutations.reset.isPending}
            isSaving={mutations.updateItems.isPending}
            isSending={mutations.send.isPending}
            hasChanges={store.hasChanges()}
            countdown={countdown}
            autoSendTime={autoSendTime}
            onDownloadCsv={handleDownload}
            onEnterEdit={handleEnterEditMode}
            onSendNow={() => setShowSendConfirm(true)}
            onAddPosition={() => setShowAddPosition(true)}
            onReset={() => setShowResetConfirm(true)}
            onCancelEdit={store.cancelEdit}
            onSave={handleSave}
          />
        </div>
      )}
      <div className="flex flex-1 flex-col overflow-hidden">
        {isLoading ? (
          <ReportTableSkeleton />
        ) : (
          <TableComponent
            report={report}
            isLoading={false}
            error={error}
            sorting={store.isEditing ? null : sorting}
            onSortChange={store.isEditing ? () => {} : setSorting}
            isEditing={store.isEditing}
            localEdits={store.localEdits}
            newItems={store.newItems}
            isMutating={isMutating}
            onToggleItemDeleted={store.toggleItemDeleted}
            onUpdateItemQuantity={store.updateItemQuantity}
            onUpdateItemCounterpartyProductId={store.updateItemCounterpartyProductId}
            onRemoveNewItem={store.removeNewItem}
            onUpdateNewItemQuantity={store.updateNewItemQuantity}
            onUpdateNewItemCounterpartyProductId={store.updateNewItemCounterpartyProductId}
            searchQuery={searchQuery}
          />
        )}
      </div>

      <ConfirmDialog
        show={showResetConfirm}
        title="Reset Changes"
        message={resetDialogMessage}
        confirmLabel="Reset"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={mutations.reset.isPending}
        onConfirm={handleReset}
        onCancel={() => setShowResetConfirm(false)}
      />

      <ConfirmDialog
        show={showSendConfirm}
        title="Send Report"
        message={sendDialogMessage}
        confirmLabel="Send"
        cancelLabel="Cancel"
        variant="primary"
        isLoading={mutations.send.isPending}
        onConfirm={handleSend}
        onCancel={() => setShowSendConfirm(false)}
      />

      <AddPositionDialog
        show={showAddPosition}
        inventoryState={report?.inventory_state}
        inventoryCounterparty={inventoryCounterparty}
        counterparty={counterparty}
        quantityField={quantityField}
        existingProductIds={existingProductIds}
        onAdd={(cpid, symbolId, qty) => {
          store.addNewItem(cpid, symbolId, qty);
        }}
        onCancel={() => setShowAddPosition(false)}
      />
    </div>
  );
}
